import React, { useEffect, useState } from 'react';
import { Modal } from 'bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { find, isEmpty, filter } from 'lodash';
import Big from 'big.js';

import CloseModalIcon from 'images/cerrar.svg';
import BackIcon from 'images/Arrow-ios-back-fill.svg';

import CatalogProductQuantity from '../../CatalogProductQuantity';
import ProductImagesCarousel from './ProductImagesCarousel';
import VariantsComponent from './VariantsComponent';

import { addLine, removeLine } from '../../../../actions/cart';
import { formatMoney } from '../../../../utils/number';
import {
  clampOrderableQuantity,
  getMaxOrderableQuantity,
  validateAvailable
} from '../../../../utils/productUtils';
import PricesComponent from '../../../Shared/PricesComponent';

const ProductModal = ({ product, onDismiss, isSmallDevice }) => {
  const { title, description = "", image_urls = [] } = product;

  const orderLines = useSelector((reduxState) => reduxState.cartReducer.orderLines || []);
  const { retailer } = useSelector((reduxState) => reduxState.retailerReducer);

  const [subtotal, setSubtotal] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [variants, setVariants] = useState([]);
  const [selectedVariants, setSelectedVariants] = useState({});
  const [openedMobilModalOptions, setOpenedMobilModalOptions] = useState(false);
  const [price, setPrice] = useState(0);
  const [images, setImages] = useState(image_urls);
  const [selectedProduct, setSelectedProduct] = useState({});

  const hasCombinations = !isEmpty(product.product_variant_combinations);
  const stockProduct = !isEmpty(selectedProduct) ? selectedProduct : product;
  const cartQuantityForProduct = isEmpty(stockProduct)
    ? 0
    : orderLines
      .filter((line) => line.product?.web_id === stockProduct.web_id)
      .reduce((sum, line) => sum + Number(line.quantity || 0), 0);
  const maxOrderableQuantity = getMaxOrderableQuantity(stockProduct);
  const remainingStock = maxOrderableQuantity === null
    ? null
    : Math.max(0, maxOrderableQuantity - cartQuantityForProduct);

  const dispatch = useDispatch();

  useEffect(() => {
    setSubtotal(Number(Big(Number(price)).times(Big(Number(quantity)))));
  }, [quantity, price]);

  useEffect(() => {
    setImages([]);
    if (product.web_id) {
      buildVariants();
      const el = document.getElementById("productModal");
      if (el) {
        const pModal = new Modal(el);
        pModal.show();
        el.addEventListener("hidden.bs.modal", onDismiss);
        el.addEventListener("hide.bs.modal", onDismiss);

        return () => el.removeEventListener("hidden.bs.modal", onDismiss);
      }
    } else {
      setQuantity(1);
    }
    return () => { };
  }, [product.web_id]);

  const getEffectivePrice = (item) => {
    if (!item || isEmpty(item)) return 0;
    const disc = Number(item.discounted_price || 0);
    const reg = Number(item.price || 0);
    if (disc > 0 && reg > 0) return Math.min(disc, reg);
    return disc > 0 ? disc : reg;
  };

  useEffect(() => {
    if (isEmpty(selectedProduct)) {
      setPrice(0);
      setImages(image_urls);
      return;
    };

    setPrice(getEffectivePrice(selectedProduct));

    if (selectedProduct.is_combination && !isEmpty(selectedProduct.attached_image)) {
      setImages([selectedProduct.attached_image]);
      return;
    };

    setImages(image_urls);
  }, [selectedProduct]);

  useEffect(() => {
    if (remainingStock === null) return;

    if (remainingStock > 0 && quantity > remainingStock) {
      setQuantity(remainingStock);
    }
  }, [remainingStock]);

  const canBeSelectedProduct = () => {
    if (hasCombinations) {
      if (!selectedProduct) return false;

      return validateAvailable(selectedProduct);
    };

    return validateAvailable(product);
  };

  const buildVariants = () => {
    setVariants(
      product.product_variant_combinations.map((variant) => ({
        ...variant,
        selected: false,
      }))
    );
  };

  const addProductToCart = (e, close = false) => {
    e.preventDefault();

    if (isEmpty(selectedProduct)) return;

    if (remainingStock !== null && (remainingStock <= 0 || quantity > remainingStock)) {
      return;
    }

    const quantityToAdd = clampOrderableQuantity(
      selectedProduct,
      remainingStock === null ? quantity : Math.min(quantity, remainingStock)
    );

    if (quantityToAdd <= 0) return;

    dispatch(addLine(selectedProduct, quantityToAdd, retailer.tax_amount));

    if (close) {
      const el = document.getElementById("productModal");
      if (el) {
        const pModal = Modal.getInstance(el);
        pModal.hide();
      }
    } else {
      window.location.href = "/checkout";
    }
  };

  const onChangeQuantity = (newQuantity) => {
    if (newQuantity) {
      const clampedQuantity = remainingStock === null
        ? newQuantity
        : Math.min(newQuantity, remainingStock);

      setQuantity(clampedQuantity || 1);
    } else {
      setQuantity(1);
      const currentLine = orderLines.find((line) => line.product?.web_id === product.web_id);
      if (currentLine) {
        dispatch(removeLine(currentLine));
      }
    }
  };

  const onClickAddAndPay = (e) => {
    addProductToCart(e, isSmallDevice);
  };

  const handleVariantOptionSelect = (option) => {
    setSelectedVariant(option);
    const variant = find(product.product_variant_combinations, {
      id: option?.value,
    });
    if (variant) {
      setPrice(getEffectivePrice(variant));
    }
  };

  const handleSelectedVariant = (e, variant) => {
    const checked = e.target.checked;

    if (checked && !find(selectedVariants, { web_id: variant.web_id })) {
      setSelectedVariants([...selectedVariants, variant]);
    } else {
      setSelectedVariants(
        filter(selectedVariants, (o) => o.web_id != variant.web_id)
      );
    }

    setVariants(
      variants.map((comb) => {
        if (comb.web_id == variant.web_id) {
          return { ...comb, selected: checked };
        }

        return comb;
      })
    );
  };

  const currentTarget = isEmpty(selectedProduct) ? product : selectedProduct;
  const rawDiscounted = Number(currentTarget?.discounted_price || 0);
  const rawPrice = Number(currentTarget?.price || 0);

  let modalCurrentPrice = rawPrice;
  let modalStrikethroughPrice = 0;

  if (rawDiscounted > 0 && rawDiscounted < rawPrice) {
    modalCurrentPrice = rawDiscounted;
    modalStrikethroughPrice = rawPrice;
  } else if (rawDiscounted > rawPrice) {
    modalCurrentPrice = rawPrice;
    modalStrikethroughPrice = rawDiscounted;
  }

  const showModalStrikethroughPrice = modalStrikethroughPrice > 0 && modalStrikethroughPrice > modalCurrentPrice;

  return (
    <div className="modal product-modal" id="productModal">
      <div className="modal-dialog modal-dialog-centered modal-xl modal-fullscreen-md-down">
        <div className="modal-content catalog-product-modal">
          <button
            type="button"
            className="btn btn-close-modal"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <img src={CloseModalIcon} alt="" />
          </button>
          <div
            className="row ps-md-3 pe-md-3 m-0 product-modal-content-row"
            style={{ minHeight: 400, zIndex: openedMobilModalOptions ? 10 : 0 }}
          >
            <div className="col-12 col-md-5 product-modal-image-container">
              <button
                type="button"
                className="btn d-md-none product-modal-back"
                style={{ zIndex: openedMobilModalOptions ? 0 : 10 }}
                onClick={onDismiss}
              >
                <img src={BackIcon} alt="" />
              </button>
              {product.web_id && (
                <ProductImagesCarousel
                  product={product}
                  images={images}
                  isSmallDevice={isSmallDevice}
                />
              )}
            </div>
            <div className="col-12 col-md-7 mt-md-4 border-start product-modal-container">
              <div className="pt-3 pt-md-2  ps-3 pe-3 product-modal-content">
                <h3 className="modal-title fw-bold border-bottom">{title}</h3>
                <PricesComponent>
                  <div className="d-flex align-items-baseline gap-2 pt-2 pb-4">
                    {showModalStrikethroughPrice && (
                      <span className="text-muted text-decoration-line-through fs-6">
                        {retailer.currency}
                        {formatMoney(modalStrikethroughPrice)}{" "}
                        {retailer.currency_code}
                      </span>
                    )}
                    <h5 className="fw-bold mb-0">
                      {retailer.currency}
                      {formatMoney(modalCurrentPrice)}{" "}
                      {retailer.currency_code}
                    </h5>
                  </div>
                </PricesComponent>
                <div id="product-description">
                  <div dangerouslySetInnerHTML={{ __html: description }} />
                </div>

                {isSmallDevice && (
                  <div className="mt-4 mb-4 product-variant-container">
                    <div key={`variant-select`} className={`d-flex flex-row`}>
                      <div className="pt-2 pb-1 w-100">
                        <VariantsComponent
                          product={product}
                          setSelectedProduct={setSelectedProduct}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {!isSmallDevice && !isEmpty(product) && (
                  <VariantsComponent
                    product={product}
                    setSelectedProduct={setSelectedProduct}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="bg-white product-modal-footer d-flex align-items-center">
            <div className="flex-grow-1 d-flex flex-column flex-sm-row align-items-center product-modal-subtotal">
              <PricesComponent>
                {quantity > 0 && (
                  <>
                    Subtotal
                    <h5 className="ps-sm-3 fw-bold mb-0">
                      {retailer.currency}
                      {formatMoney(Number(subtotal))} {retailer.currency_code}
                    </h5>
                  </>
                )}
              </PricesComponent>
            </div>
            {quantity > 0 && canBeSelectedProduct() && (remainingStock === null || remainingStock > 0) && (
              <CatalogProductQuantity
                quantity={quantity}
                maxQuantity={remainingStock}
                onChangeQuantity={onChangeQuantity}
              />
            )}

            {canBeSelectedProduct() && (
              <>
                <button
                  type="button"
                  className="btn text-muted d-flex flex-row align-items-center justify-content-center text-white me-md-1 s-btn-secondary"
                  onClick={onClickAddAndPay}
                  disabled={isEmpty(selectedProduct) || (remainingStock !== null && remainingStock <= 0)}
                >
                  Agregar
                  {!isSmallDevice && " y pagar"}
                </button>
                <button
                  type="button"
                  className="btn btn-main text-white d-none d-md-block"
                  onClick={(e) => addProductToCart(e, true)}
                  disabled={isEmpty(selectedProduct) || (remainingStock !== null && remainingStock <= 0)}
                >
                  Agregar y seguir comprando
                </button>
              </>
            )}

            {!canBeSelectedProduct() && (
              <>
                <button
                  type="button"
                  className="btn text-muted me-md-1 sold-out-btn"
                >
                  Agotado
                </button>
                <button
                  type="button"
                  className="btn text-muted me-md-1 sold-out-btn solid"
                >
                  Agotado
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductModal;
